import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter, finalize, map, Observable, of, switchMap, tap } from 'rxjs';

import { IdValueType, KeyValueIsDefaultModel, KeyValueRoleModel, KeyValueType } from '@models/common';
import { CipoHttpErrorResponse } from '@models/error/cipo-error.model';
import { RouteCodeEnum } from '@models/menu';
import { FormValueType, MimeTypesEnum } from '@models/module/fields/main';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { Permission } from '@models/permission';
import { DocumentActionsData, SubmittableForm, TransitionFormValue } from '@module-definition/document/common';
import { DocumentHeaderComponent } from '@module-definition/document/document-header/document-header.component';
import { DynamicFormComponent } from '@module-definition/document/dynamic-form/dynamic-form.component';
import {
  HorizontalSplitterComponent,
  PaneOptions,
} from '@module-definition/horizontal-splitter/horizontal-splitter.component';
import { AssignmentsComponent } from '@module-instance/assignments/assignments.component';
import { AwaitingApprovalComponent } from '@module-instance/awaiting-approval/awaiting-approval.component';
import {
  DmFieldsService,
  DmGridFields,
  DmService,
  DmUrlActionEnum,
  EntityInstanceTransition,
  TransitionEnum,
} from '@module-instance/dynamic-module/common';
import { DocumentSidebarComponent } from '@module-instance/dynamic-module/document-details/document-sidebar/document-sidebar.component';
import { DmActionsService } from '@module-instance/dynamic-module/dynamic-module.actions.service';
import { EntityTemplatesService, ModuleInstanceService, PresetDocument } from '@module-instance/services';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { ProjectsService } from '@module-instance/services/projects.service';
import { EntityTableRow } from '@shared/components/data-list/common';
import { mapFieldErrors } from '@shared/components/fields/common';
import { LoadErrorComponent } from '@shared/components/load-error/load-error.component';
import { DEBOUNCE_TIMES, DROPDOWN_ITEMS, DropdownActionsEnum, DropdownItem } from '@shared/consts';
import { ConfirmationDialog, ConfirmationDialogData } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules';
import { NotificationService, UserService } from '@shared/services';
import { A11yAnnouncerService } from '@shared/services/a11y-announcer.service';
import { DocumentService } from '@shared/services/document.service';
import { AiStore, DictionaryStore, UserStore } from '@signal-store/index';
import { makeKey } from '@signal-store/services/dictionary.service';
import {
  DocumentDetailsDialogData,
  DocumentDetailsQueryParams,
  DocumentDetailsService,
  DocumentDetailsSessionStore,
  DocumentDetailsStore,
  DocumentInfoModel,
} from './common';
import { DetailsTransitionComponent } from './details-transition/details-transition.component';
import { DocumentDetailsTabsComponent } from './document-details-tabs/document-details-tabs.component';

@Component({
  selector: 'cipo-document-details',
  templateUrl: './document-details.component.html',
  imports: [
    MatExpansionModule,
    MatMenuModule,
    MatTabsModule,
    NgTemplateOutlet,
    forwardRef(() => DocumentDetailsTabsComponent),
    CipoDialogModule,
    DocumentHeaderComponent,
    AwaitingApprovalComponent,
    DetailsTransitionComponent,
    DynamicFormComponent,
    HorizontalSplitterComponent,
    DocumentSidebarComponent,
    AssignmentsComponent,
    LoadErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDetailsComponent implements OnInit {
  private router = inject(Router);
  private notify = inject(NotificationService);
  private moduleInstanceService = inject(ModuleInstanceService);
  private moduleInstanceCommonService = inject(ModuleInstanceCommonService);
  private dmFieldsService = inject(DmFieldsService);
  private documentDetailsService = inject(DocumentDetailsService);
  private entityTemplateService = inject(EntityTemplatesService);
  private dmActionsService = inject(DmActionsService);
  private documentService = inject(DocumentService);
  private projectsService = inject(ProjectsService);
  private dialog = inject(MatDialog);
  private dmService = inject(DmService);
  private dictionaryStore = inject(DictionaryStore);
  private aiStore = inject(AiStore);
  private userService = inject(UserService);
  private announcer = inject(A11yAnnouncerService);
  private translate = inject(TranslateService);
  userStore = inject(UserStore);
  dmStore = inject(DocumentDetailsStore);
  private sessionStore = inject(DocumentDetailsSessionStore);

  /** Read once in `ngOnInit`; later changes are ignored. */
  readonly data = input.required<DocumentDetailsDialogData>();
  readonly closeRequested = output<Partial<DocumentInfoModel> | void>();
  /** Whether a save started from `handleSubmit` went through. Emitted only for the plain document
   * update; transitions report themselves through the store and the activity tab. */
  readonly saveResult = output<boolean>();

  dynamicFormRef = viewChild<DynamicFormComponent>('dynamicForm');
  transitionFormRef = viewChild<SubmittableForm>('transitionFormComponent');
  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  /** Computed doesn't work here because it's triggered after input is open */
  fileType = signal<MimeTypesEnum>(null);
  fileAccept = computed(() => {
    switch (this.fileType()) {
      case MimeTypesEnum.XER:
        return '.xer';
      case MimeTypesEnum.XML:
        return '.xml';
      default:
        return null;
    }
  });

  form = new FormGroup({});

  readonly formDirty = toSignal(this.form.events.pipe(map(() => this.form.dirty)), { initialValue: false });
  transitionForm = new FormGroup({});
  /** For draft documents, that have related documents (in transitions), and does not have the field on the screen */
  relatedDocuments: IdValueType<number | number[]>[] = [];
  autosaveInProgress = signal(false);
  submitHasErrors = signal(false);
  markedForAutosave = signal(false);
  /** Selected document's entityInstanceId */
  selectedEntityInstanceId = signal<number>(null);
  currentDocument = computed(() => this.dmStore.documents()[this.selectedEntityInstanceId()]);

  private readonly moduleStartScreens = computed(() =>
    this.sessionStore.startScreensModuleId() === this.data().moduleId ? this.sessionStore.startScreens() : [],
  );
  startScreens = computed<Record<number, string>>(() => {
    return this.moduleStartScreens().reduce((acc, screen) => {
      acc[screen.key] = screen.description;
      return acc;
    }, {});
  });
  private readonly moduleName = computed(
    () => this.userStore.menuItemsList().find(mi => mi.moduleId === this.data().moduleId)?.name,
  );

  readonly defaultStartScreenId = computed(() => this.moduleStartScreens()[0]?.key);
  info = computed(() => this.currentDocument()?.info);
  compactTransitionForm = signal(false);
  transitionStarted = computed(() => !!this.currentDocument()?.transitionData);
  workflowGroups = computed(() => this.gridFields()?.workflowGroups ?? []);
  /** The document's own group, falling back to the default one for a document that has none yet. */
  private workflowGroupSeed = computed(
    () => this.info()?.workflowGroupId ?? this.workflowGroups().find(wg => wg.isDefault)?.key,
  );
  workflowGroupId = linkedSignal(() => this.workflowGroupSeed());
  workflowGroupName = computed(() => this.workflowGroups().find(wg => wg.key === this.workflowGroupId())?.value);
  /** Contracts only, and only where the module is configured to manage several groups. The legacy
   * screen offered this on any such module; it is kept to contracts on purpose. */
  showWorkflowGroups = computed(
    () =>
      this.data().moduleId === SystemModulesEnum.Contracts &&
      this.workflowGroups().length > 1 &&
      !!this.userStore.menuItemsList().find(mi => mi.moduleId === this.data().moduleId)?.manageWorkflowGroups,
  );
  /** A draft picks freely; an existing document only while it is being edited. */
  canChangeWorkflowGroup = computed(
    () => this.showWorkflowGroups() && !this.transitionStarted() && (this.isDraft() || this.editing()),
  );
  /* If the document is in a transition, the right pane in shown */
  leftPaneCollapsed = linkedSignal(() => {
    return this.transitionStarted() && !this.isDraft();
  });
  leftPaneOptions = computed<PaneOptions>(() => {
    return {
      initialWidth: this.transitionStarted() ? (this.isDraft() ? 0.7 : this.leftPaneCollapsed() ? 0.05 : 0.3) : 1,
      minWidth: 0.05,
      maxWidth: 0.75,
    };
  });
  gridFields = signal<DmGridFields>(null);
  /** The fields did not arrive: either their own request failed, or the preset values they wait for did. */
  private fieldsFailed = signal(false);
  /** What the fields were last asked for, so a retry asks for exactly the same thing. */
  private lastTemplateId: number;
  /** The preset poll never ends on its own, so a second load has to stop the first. */
  private presetPollId: ReturnType<typeof setInterval>;
  /** Nothing left to render: no fields means no form, no information means no header and no actions. A
   * failed transitions call is deliberately not one of them - it only costs the workflow buttons. */
  readonly loadFailed = computed(() => this.fieldsFailed() || this.currentDocument()?.loadingGeneralInfo === 'error');
  fieldNameToIdDict = computed(() => {
    const { fields } = this.gridFields() ?? {};
    return fields?.reduce((acc, { fieldOptions: { name }, id }) => {
      if (!name) {
        return acc;
      }
      const key = name[0].toLowerCase() + name.slice(1);
      acc[key] = id;
      return acc;
    }, {});
  });
  /* This is saved locally, we don't need preset data in store */
  presetData = signal<PresetDocument>(null);
  selectedTransition = signal<EntityInstanceTransition>(null);
  /* Document number set by user */
  docNumber = linkedSignal<KeyValueType<number>>(() => {
    const info = this.info();
    if (!info) {
      return null;
    }
    const { documentNumber, documentNumberId, moduleDocumentNumber } = info;
    return { key: documentNumberId, value: documentNumber ?? moduleDocumentNumber };
  });
  submitting = signal(false);
  selectedTabIndex = signal(0);
  assignee = signal<KeyValueRoleModel<string, string>>(null);
  transitions = signal<EntityInstanceTransition[]>(null);
  filteredTransitions = computed(() => {
    if (this.info()?.approvalId) {
      return [];
    }
    const isDraft = this.isDraft();
    /* A related document needs a saved parent to be linked to: the server call is skipped while the
      document is a draft, and the local `relatedDocuments` fallback only travels with `saveDraft`. */
    const hasId = !!this.selectedEntityInstanceId();
    return this.transitions()
      ?.filter(({ otherModule }) => hasId || !otherModule)
      .map(({ roles, ...rest }) => ({
        ...rest,
        /* Only show roles for transitions that are entering the workflow (draft -> not_draft) */
        roles: isDraft && !rest.isDraft ? roles : [roles?.[0]],
      }));
  });
  /** Includes new document and draft documents that are not in approval */
  isDraft = computed(() => {
    const { isDraftState, approvalId } = this.info() ?? {};
    return this.info() ? isDraftState && !approvalId : !this.selectedEntityInstanceId();
  });
  showTabs = computed(() => {
    const isProgramOrProject = [SystemModulesEnum.Programs, SystemModulesEnum.Projects].includes(this.data().moduleId);
    const info = this.info();
    if (!info || info.isDraftState || !isProgramOrProject) {
      return false;
    }
    return true;
  });
  /** Whether the main form is in edit mode */
  editing = linkedSignal(() => {
    const generalInfo = this.info();
    /* If it's a preset document, editing is always true.
        If selectedEntityInstanceId is 0, it's a new document */
    if (this.presetData() || this.selectedEntityInstanceId() === 0) {
      return true;
    }
    if (!generalInfo) {
      return false;
    }
    return generalInfo.isDraftState && !generalInfo.approvalId && !this.transitionStarted();
  });

  // Consume the request once so saves and sidebar navigation do not re-enter edit mode.
  private initialEditPending = true;

  private readonly openInEditModeEffect = effect(() => {
    const info = this.info();
    if (!info || !this.initialEditPending) return;
    this.initialEditPending = false;
    if (
      this.data().openInEditMode &&
      this.selectedEntityInstanceId() === this.data().entityInstanceId &&
      info.canUpdate &&
      !info.approvalId &&
      !this.transitionStarted()
    ) {
      untracked(() => this.editing.set(true));
    }
  });

  /** Data used to control the document header */
  headerData = computed(() =>
    this.documentDetailsService.getHeaderData({
      moduleId: this.data().moduleId,
      presetData: this.presetData(),
      info: this.info(),
    }),
  );

  // MARK: Footer Actions
  /** Data used to control the actions bar */
  actionsData = computed<DocumentActionsData>(() => {
    const { contractId, moduleId } = this.data();
    const info = this.info();
    const entityInstanceId = this.selectedEntityInstanceId();
    if (!info && entityInstanceId) {
      return null;
    }
    const { canChangeAssignment } = this.gridFields() ?? {};
    const { isDraftState, isClosedState, isClosed, isArchived, permissions, isRevision } = info ?? {};
    const selectedTransition = this.selectedTransition();
    const { assignee, manualAssignUsers: documentManualAssignUsers } = this.currentDocument().assignment ?? {};
    const transitionData = this.currentDocument().transitionData;
    const manualAssignUsers = transitionData?.gridFields?.manualAssignUsers ?? documentManualAssignUsers;
    const requiredFormUnavailable = this.transitionStarted()
      ? !this.transitionFormRef() || (this.isDraft() && !this.dynamicFormRef())
      : !this.dynamicFormRef();
    return {
      moduleId,
      contractId,
      assignee,
      assignOptions: transitionData?.gridFields?.manualAssignUsers ?? manualAssignUsers,
      disableActions: this.submitting() || this.autosaveInProgress() || requiredFormUnavailable,
      submitLabel: this.editing() && !this.isDraft() ? 'actions.save' : selectedTransition?.name || null,
      entityInstanceId,
      /* Only show document number field if it's a new document or if it's a draft (and not in approval) */
      alwaysShowDocNum: !isRevision && (!entityInstanceId || isDraftState),
      disableDocNum:
        (!this.selectedEntityInstanceId() && this.data().urlActionId === DmUrlActionEnum.Preset) || !!info?.approvalId,
      canAssign: canChangeAssignment && !isClosedState && !isClosed && !isArchived && manualAssignUsers?.length > 0,
      hideAssign:
        this.editing() ||
        !!isClosedState ||
        !!isClosed ||
        !!isArchived ||
        !!info?.approvalId ||
        !permissions?.includes(Permission.Assignments),
      documentNumber: this.docNumber(),
      /* Show cancel button if the document entered the workflow and editing is true
        Or if transition is started
      */
      showCancel: !!selectedTransition || (this.editing() && !this.isDraft()),
      transitionStarted: !!selectedTransition,
      disableMoreActions: entityInstanceId && !isDraftState && this.editing(),
      formInvalid: this.submitHasErrors(),
    };
  });

  /** More actions (more_vert after save button) */
  moreActions = computed(() => {
    const actions: DropdownItem[] = [];
    const {
      isDraftState,
      isClosedState,
      approvalId,
      isRevision,
      entityInstanceId,
      canUpdate,
      canChangeNumber,
      isContractClosedOrArchived,
      userIsInitiator,
      canDelete,
      canPrint,
    } = this.info() ?? {};
    const isTemplate = !!this.presetData();
    if (!entityInstanceId || this.transitionStarted()) {
      return [];
    }

    if (canUpdate && !this.editing() && !approvalId) {
      actions.push(DROPDOWN_ITEMS.EDIT);
    }
    if (!isDraftState && !isTemplate && !approvalId && !isRevision && canChangeNumber) {
      actions.push(DROPDOWN_ITEMS.CHANGE_DOC_NUM);
    }

    const startScreens = this.moduleStartScreens();
    if (!isContractClosedOrArchived && userIsInitiator && startScreens.length) {
      actions.push({
        ...DROPDOWN_ITEMS.CREATE_NEW,
        labelParams: { module: this.moduleName() },
        options:
          startScreens.length > 1
            ? startScreens.map(({ key, value, description }) => ({ key, label: description ?? value }))
            : undefined,
      });
    }

    if (!isContractClosedOrArchived && userIsInitiator && this.gridFields()?.fields?.length) {
      actions.push(DROPDOWN_ITEMS.DUPLICATE);
    }

    if (!approvalId && canDelete) {
      actions.push(DROPDOWN_ITEMS.DELETE);
    }

    if (actions.length) {
      actions.push(DROPDOWN_ITEMS.DIVIDER);
    }
    if (canPrint) {
      actions.push(DROPDOWN_ITEMS.EMAIL, DROPDOWN_ITEMS.DOWNLOAD, DROPDOWN_ITEMS.PRINT);
    }
    if (this.showTabs()) {
      actions.push(DROPDOWN_ITEMS.DIVIDER, DROPDOWN_ITEMS.IMPORT_PRIMAVERA);
      actions.push(DROPDOWN_ITEMS.IMPORT_MICROSOFT_PROJECT);
    }
    if (this.userStore.isCipoAdminOnCurrentContractOrGlobal() && isClosedState && !isTemplate && canPrint) {
      actions.push(DROPDOWN_ITEMS.DIVIDER, DROPDOWN_ITEMS.REGENERATE_PDF);
    }
    return actions;
  });

  private rolledBack = computed(() => this.currentDocument()?.loadingRollback === 'success');

  constructor() {
    effect(() => {
      if (this.rolledBack()) {
        untracked(() => {
          const { contractId, moduleId, entityInstanceId, workflowId } = this.currentDocument().params;
          this.loadTransitions({ contractId, moduleId, entityInstanceId, workflowId });
          /* This is needed to relaod field values in case there was any blue fields on transition form  */
          this.loadGridFields();
        });
      }
    });

    this.form.valueChanges
      .pipe(
        tap(() => {
          if (this.form.valid && this.submitHasErrors()) {
            this.submitHasErrors.set(false);
          }
          this.markedForAutosave.set(true);
        }),
        debounceTime(DEBOUNCE_TIMES.fiveSeconds),
        filter(() => !this.submitting() && !this.transitionStarted()),
        filter(() => this.isDraft() && this.gridFields()?.autosaveEnabled),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.autosaveInProgress.set(true);
        this.markedForAutosave.set(false);
        this.submitting.set(true);
        this.saveDraft(this.dynamicFormRef().getFormValue(), true);
      });

    this.transitionForm.valueChanges
      .pipe(
        tap(() => {
          if (this.transitionForm.valid && this.submitHasErrors()) {
            this.submitHasErrors.set(false);
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.setupDocumentData();
  }

  toggleAiAssistant() {
    this.aiStore.toggleOpen();
  }

  // MARK: Initial setup
  private setupDocumentData() {
    const { entityInstanceId, templateId, urlActionId, showSidebar, ...dialogParams } = this.data();
    const documentParams = { ...dialogParams, entityInstanceId };
    this.selectedEntityInstanceId.set(entityInstanceId);

    this.dmStore.setDocumentParams(documentParams);
    this.loadDocumentData(urlActionId === DmUrlActionEnum.Clone ? templateId : undefined);
  }

  /** The initial load of the selected document, and what {@link retryLoad} replays when it failed. */
  private loadDocumentData(templateId?: number) {
    const { contractId, workflowId, moduleId, entityInstanceId } = this.currentDocument().params;
    const isPreset = !entityInstanceId && this.data().urlActionId === DmUrlActionEnum.Preset;
    this.loadGridFields(templateId, isPreset);
    this.loadTransitions({ contractId, workflowId, entityInstanceId, moduleId });
    if (entityInstanceId) {
      this.dmStore.loadGeneralInformation({ entityInstanceId, contractId, moduleId });
    } else if (isPreset && !this.presetData()) {
      this.entityTemplateService.getPresetDocumentDetails({ id: this.data().templateId, moduleId }).subscribe({
        next: presetData => {
          this.presetData.set(presetData);
          this.docNumber.set({ key: presetData.documentNumber, value: presetData.moduleDocumentNumber });
        },
        error: () => this.fieldsFailed.set(true),
      });
    }
  }

  /** Offered in place of the loading skeleton once {@link loadFailed} is set. */
  retryLoad() {
    this.loadDocumentData(this.lastTemplateId);
  }

  /** Load document grid fields.
   * @param templateId the document to copy the fields and values from, which goes to another endpoint.
   * @param awaitPresetValues only the initial load of a preset document races the preset request; every
   * later load either has the values already or is for a document that has none.
   */
  private loadGridFields(templateId?: number, awaitPresetValues = false) {
    const { contractId, screenId, workflowId, moduleId, entityInstanceId } = this.currentDocument().params;
    this.gridFields.set(null);
    this.fieldsFailed.set(false);
    this.lastTemplateId = templateId;
    this.dmFieldsService
      .getGridFields(
        { contractId, screenId, workflowId, moduleId, entityInstanceId: templateId ?? entityInstanceId },
        !!templateId,
      )
      .subscribe({
        next: ({ fields, ...rest }) => {
          this.resetForm(this.form, false);
          if (awaitPresetValues) {
            /* Sh!tty sollution until BE will return preset values on gridFields */
            /* A retry gets here a second time, and the poll of the abandoned attempt never ends. */
            clearInterval(this.presetPollId);
            const intervalId = setInterval(() => {
              if (this.presetData()?.valuesLookup) {
                clearInterval(intervalId);

                const presetValues = this.presetData()?.valuesLookup as FormValueType;
                this.gridFields.set({
                  ...rest,
                  fields: this.dmFieldsService.applyFieldValues(fields, presetValues),
                });
              }
            }, 100);
            this.presetPollId = intervalId;
            /* End of Sh!tty sollution */
          } else {
            this.gridFields.set({ ...rest, fields });
          }
        },
        error: () => this.fieldsFailed.set(true),
      });
  }

  private loadTransitions(
    params: Pick<DocumentDetailsQueryParams, 'entityInstanceId' | 'contractId' | 'moduleId' | 'workflowId'>,
  ) {
    this.moduleInstanceService.getTransition(params).subscribe(transitions => this.transitions.set(transitions));
  }

  // MARK: Sidebar actions
  /* The number comes off the row: `info` is blanked for the length of the reload the pick starts. */
  onSidebarSelect(row: EntityTableRow) {
    this.selectDocument(row.entity_instance_id, row.screen_id);

    const documentNumber = row.full_doc_num;
    if (documentNumber) {
      this.announcer.announce(
        this.translate.instant('a11y.documentDetails.documentOpened', { number: documentNumber }),
      );
    }
  }

  selectDocument(entityInstanceId: number, docScreen?: number, silent = false) {
    /* This method is used to select a document from the sidebar, so the contract, module and workflow is the same for all documents */
    const { moduleId, contractId, workflowId } = this.data();
    /* For revisions, the screenId is the same as current document */
    const screenId = docScreen || this.currentDocument().params.screenId;
    const params = { entityInstanceId, moduleId, screenId, contractId, workflowId };
    this.selectedEntityInstanceId.set(entityInstanceId);
    this.dmStore.setDocumentParams(params);
    this.updateDetailsUrl({
      newEntityInstanceId: entityInstanceId,
      screenId: docScreen,
      /* Be aware of this if we want to add sidebar for preset documents. This might cause problem when switching from a preset to other document */
      actionId: this.presetData() ? DmUrlActionEnum.Preset : null,
    });
    this.dmStore.loadGeneralInformation({ entityInstanceId, moduleId, contractId });
    if (silent) {
      return;
    }
    this.gridFields.set(null);
    this.loadTransitions(params);
    this.loadGridFields();
  }

  // MARK: Actions handles
  onAssignmentChanged(assignee: KeyValueRoleModel<string, string>) {
    /* Will be included in the transition */
    if (this.transitionStarted()) {
      this.assignee.set(assignee);
      return;
    }

    const { contractId, entityInstanceId, moduleId } = this.currentDocument().params;
    this.dmStore.loadActivityInstances({ contractId, moduleId, entityInstanceId });
    this.setDocumentAsDirty();
  }

  docNumChanged(documentNumber: KeyValueType<number>) {
    this.docNumber.set(documentNumber);
    /* For drafts, the docNum will be sent on submit */
    if (this.info()?.isDraftState === false) {
      this.reloadDocument();
      this.setDocumentAsDirty();
    }
  }

  assignmentChanged() {
    this.userStore.setReloadContext(true);
  }

  handleMoreActions(action: DropdownActionsEnum) {
    const {
      info,
      params: { moduleId },
    } = this.currentDocument();
    const document = { ...info, moduleId } as DocumentInfoModel;
    switch (action) {
      case DropdownActionsEnum.EDIT:
        this.editing.set(true);
        break;
      case DropdownActionsEnum.CREATE_NEW:
        this.createNewDocument();
        break;
      case DropdownActionsEnum.DUPLICATE:
        this.duplicateDocument();
        break;
      case DropdownActionsEnum.EMAIL:
        this.dmActionsService.emailDocuments([document]);
        break;
      case DropdownActionsEnum.DOWNLOAD:
        this.dmActionsService.downloadDocuments([document]);
        break;
      case DropdownActionsEnum.PRINT:
        this.dmActionsService.printDocuments([document]);
        break;
      case DropdownActionsEnum.IMPORT_PRIMAVERA:
        this.openFileInput(MimeTypesEnum.XER);
        break;
      case DropdownActionsEnum.IMPORT_MICROSOFT_PROJECT:
        this.openFileInput(MimeTypesEnum.XML);
        break;
      case DropdownActionsEnum.DELETE:
        this.deleteDocument();
        break;
      case DropdownActionsEnum.REGENERATE_PDF:
        this.regeneratePdf();
        break;
    }
  }

  /** The counterpart of {@link handleMoreActions} for the actions rendered as a submenu. */
  handleMoreActionOption({ id, optionKey }: { id: DropdownActionsEnum; optionKey: number }) {
    if (id === DropdownActionsEnum.CREATE_NEW) {
      this.createNewDocument(optionKey);
    }
  }

  openFileInput(mimeType: MimeTypesEnum) {
    this.fileType.set(mimeType);
    /* This timeout is required to let the fileAccept to be computed before triggering the file input click */
    setTimeout(() => {
      this.fileInput().nativeElement.click();
    }, 10);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    input.value = '';
    switch (this.fileType()) {
      case MimeTypesEnum.XER:
        this.projectsService
          .importXerFile(this.selectedEntityInstanceId(), file)
          .subscribe(() => this.notify.success('sov.fileImported'));
        break;
      case MimeTypesEnum.XML:
        this.projectsService
          .importMSProjectFile(this.selectedEntityInstanceId(), file)
          .subscribe(() => this.notify.success('sov.fileImported'));
        break;
    }
  }

  // MARK: Miscelanious
  reloadDocument() {
    const { contractId, moduleId, entityInstanceId, workflowId } = this.currentDocument().params;
    this.dmStore.loadGeneralInformation({ contractId, moduleId, entityInstanceId });
    this.loadTransitions({ contractId, moduleId, entityInstanceId, workflowId });
    this.loadGridFields();
    this.selectedTransition.set(null);
    this.submitting.set(false);
    this.resetForm(this.transitionForm);
  }

  handleDocumentReviewed() {
    this.sessionStore.setDocumentAsDirty(this.selectedEntityInstanceId());
    if (this.closeIfOpenedFromDashboard()) {
      return;
    }
    this.reloadDocument();
  }

  setDocumentAsDirty() {
    this.sessionStore.setDocumentAsDirty(this.selectedEntityInstanceId());
  }

  private isOpenedFromDashboard(): boolean {
    return this.userStore.menu.activeMenuItem()?.code === RouteCodeEnum.Dashboard;
  }

  private closeIfOpenedFromDashboard(): boolean {
    if (this.isOpenedFromDashboard()) {
      this.closeRequested.emit();
      return true;
    }
    return false;
  }

  resetForm(form: FormGroup, emitEvent = true) {
    Object.keys(form.controls).forEach(key => form.removeControl(key, { emitEvent }));
    form.markAsPristine();
  }

  discardChanges() {
    if (this.transitionStarted()) {
      this.dmStore.cancelTransition(this.selectedEntityInstanceId());
      this.selectedTransition.set(null);
      this.resetForm(this.transitionForm);
      if (this.isDraft()) {
        this.editing.set(true);
      }
      return;
    }
    this.editing.set(false);
    this.resetForm(this.form);
    this.workflowGroupId.set(this.workflowGroupSeed());
    this.dynamicFormRef().initForm(this.gridFields()?.fields);
  }

  setWorkflowGroup(group: KeyValueIsDefaultModel<number, string>) {
    if (group.key === this.workflowGroupId()) {
      return;
    }
    if (this.isDraft()) {
      this.workflowGroupId.set(group.key);
      this.form.markAsDirty();
      this.form.updateValueAndValidity();
      return;
    }
    this.dialog
      .open<ConfirmationDialog, ConfirmationDialogData>(ConfirmationDialog, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'dm.details.changeWorkflowTitle',
          description: 'dm.details.changeWorkflowMessage',
          params: { current: this.workflowGroupName(), next: group.value },
          yesLabel: 'actions.confirm',
          noLabel: 'actions.cancel',
        },
      })
      .afterClosed()
      .pipe(filter(confirmed => !!confirmed))
      .subscribe(() => this.workflowGroupId.set(group.key));
  }

  /** Another document of the same module, without closing the dialog. `screenId` is the start screen
   * picked from the action's submenu, defaulting to the only one when there was nothing to pick.
   *
   * Not routed through {@link selectDocument}: that always loads the general information, and there is
   * none for an id of 0. */
  createNewDocument(screenId: number = this.defaultStartScreenId()) {
    if (!screenId) {
      return;
    }
    const { moduleId, contractId, workflowId } = this.data();
    this.relatedDocuments = [];
    this.selectedTransition.set(null);
    this.submitHasErrors.set(false);
    this.assignee.set(null);

    this.dmStore.startNewDocument({ entityInstanceId: 0, moduleId, contractId, screenId, workflowId });
    this.selectedEntityInstanceId.set(0);
    this.loadTransitions({ contractId, workflowId, moduleId, entityInstanceId: 0 });
    this.updateDetailsUrl({ newEntityInstanceId: 0, screenId, actionId: DmUrlActionEnum.Create });
    /* No templateId, so the fields come back empty instead of copied from the open document. */
    this.loadGridFields();

    /* No toast follows a creation, and most hosts render no sidebar to show the pinned row. */
    this.announcer.announce(this.translate.instant('a11y.documentDetails.newDocument', { module: this.moduleName() }));
  }

  duplicateDocument() {
    const sourceEntityInstanceId = this.selectedEntityInstanceId();
    this.dmStore.duplicateDocument(sourceEntityInstanceId);
    this.selectedEntityInstanceId.set(0);
    this.loadTransitions({ ...this.currentDocument().params });
    this.updateDetailsUrl({ newEntityInstanceId: sourceEntityInstanceId, actionId: DmUrlActionEnum.Clone });
    this.loadGridFields(sourceEntityInstanceId);
  }

  deleteDocument() {
    return this.dialog
      .open<ConfirmationDialog, ConfirmationDialogData>(ConfirmationDialog, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'dm.deleteDocuments.title',
          description: 'dm.deleteDocuments.description',
        },
      })
      .afterClosed()
      .pipe(
        filter(confirmed => !!confirmed),
        tap(() => {
          this.submitting.set(true);
          this.editing.set(false);
        }),
        switchMap(() =>
          this.dmService.deleteDocuments([
            {
              module_id: this.currentDocument().params.moduleId,
              contract_id: this.currentDocument().params.contractId,
              entity_instance_id: this.selectedEntityInstanceId(),
            },
          ]),
        ),
      )
      .subscribe({
        next: () => {
          this.notify.success('dm.deleteDocuments.deletedMessage');
          this.sessionStore.setReloadTable(true);
          this.closeRequested.emit();
        },
        error: () => {
          this.editing.set(true);
          this.submitting.set(false);
        },
      });
  }

  private regeneratePdf() {
    const { entityInstanceId } = this.currentDocument().params;
    this.moduleInstanceService.regeneratePdfs([entityInstanceId]).subscribe({
      next: () => this.notify.success('messages.pdfRegenerationStarted'),
    });
  }

  private checkFormValid(form: FormGroup): boolean {
    form.markAllAsTouched();
    Object.values(form.controls).forEach(control => {
      const informativeMessage = control.errors?.['informative'] ? control.errors['custom'] : null;
      control.updateValueAndValidity({ emitEvent: false });
      if (control.valid && informativeMessage) {
        control.setErrors({ custom: informativeMessage, informative: true });
      }
    });
    const hasBlockingErrors = Object.values(form.controls).some(
      control => control.invalid && !control.errors?.['informative'],
    );
    if (hasBlockingErrors) {
      this.submitting.set(false);
      this.submitHasErrors.set(true);
    }
    return !hasBlockingErrors;
  }

  /** Update the url after saving a document / draft. Only should update if the url reflects the current document.
   * Don't update for relations or documents opened from other places than details-page */
  private updateDetailsUrl({
    newEntityInstanceId,
    screenId,
    actionId,
  }: {
    newEntityInstanceId: number;
    screenId?: number;
    actionId?: DmUrlActionEnum;
  }) {
    /* Only the documents opened from details-page can show sidebar, and should update the URL */
    if (!this.data().showSidebar) {
      return;
    }
    const routeParts = this.router.routerState.snapshot.url.split('/');
    const length = routeParts.length;
    const newId = newEntityInstanceId.toString();
    routeParts[length - 3] = actionId ?? '0';
    routeParts[length - 2] = newId;
    if (screenId) {
      routeParts[length - 1] = screenId.toString();
    }
    this.router.navigate(routeParts);
  }

  /** Loads the sidebar, updates the URL and selects the document. Used to refresh data & document after adding a new document */
  private reloadAfterAddNew(newEntityInstanceId: number, silent = false) {
    const sort = this.sessionStore.searchViewModel()?.sort;
    this.sessionStore.setSidebarSearchViewModel({ sort });
    this.sessionStore.setReloadTable(true);
    this.dmStore.requestSidebarReload();
    this.selectDocument(newEntityInstanceId, null, silent);
  }

  onResize(resizePercent: number) {
    if (this.compactTransitionForm() && resizePercent < 50) {
      this.compactTransitionForm.set(false);
    } else if (!this.compactTransitionForm() && resizePercent >= 50) {
      this.compactTransitionForm.set(true);
    }
    if (resizePercent < 30) {
      this.leftPaneCollapsed.set(true);
    } else if (this.leftPaneCollapsed()) {
      this.leftPaneCollapsed.set(false);
    }
  }

  /* MARK: Formulas Check
  Submit action click 
  This handles submit button click for open documents or transitions
  It's calling the child component's submit to check formulas */
  handleSubmit() {
    /* If it's a document update */
    if (this.editing()) {
      this.submitting.set(true);
      this.editing.set(false);

      /* Trigger the submit inside child component to check for formulas. This will trigger onUpdatesSubmit() */
      this.dynamicFormRef().submit();
      return;
    } else {
      /* It's a transition submit */
      this.transitionFormRef().submit();
    }
  }

  /* Called on output of dynamic form component, after the formulas are calculated */
  onUpdatesSubmit(formValue: IdValueType<string, number>[]) {
    if (this.selectedTransition()) {
      switch (this.selectedTransition().id) {
        case TransitionEnum.SaveDraft: {
          this.saveDraft(formValue);
          break;
        }
        default: {
          /* Execute a transition from draft. Exit the method if transition form is not valid */
          if (!this.checkFormValid(this.form)) {
            this.editing.set(true);
            this.selectedTransition.set(null);
            return;
          }
          if (this.selectedTransition().isAutoTrigger) {
            this.executeTransition({ values: formValue });
          } else {
            this.editing.set(false);
            this.submitting.set(false);
            this.dmStore
              .startTransition(this.selectedTransition(), this.selectedEntityInstanceId(), formValue)
              .subscribe(transitionData => {
                if (
                  !transitionData.gridFields?.fields?.length &&
                  !transitionData.notificationData?.notifyOptions?.length
                ) {
                  this.executeTransition({});
                }
              });
          }
          break;
        }
      }
    } else if (this.isDraft()) {
      this.saveDraft(formValue);
    } else {
      /* Make the form editable again and exit the method if form is not valid. */
      if (!this.checkFormValid(this.form)) {
        this.editing.set(true);
        this.saveResult.emit(false);
        return;
      }
      this.saveDocument(formValue);
    }
  }

  /* Handles the click of the transition inside Actions Dropdown */
  handleTransitionClick(transition: EntityInstanceTransition) {
    this.selectedTransition.set(transition);
    if (this.isDraft() && transition.id !== TransitionEnum.SaveDraft) {
      this.editing.set(false);
      /* Set the compact transition form, because we don't have fields - only the notification form */
      this.compactTransitionForm.set(true);
    }
    const { contractId, workflowId, entityInstanceId, moduleId, screenId } = this.currentDocument().params;
    if (this.isDraft() && !transition.otherModule) {
      this.submitting.set(true);
      /* Trigger the submit inside child component to check for formulas. This will trigger onUpdatesSubmit() */
      return this.dynamicFormRef().submit();
    }
    if (transition.isRevise) {
      /* For revise, we don't have a transition form, just start the transition */
      this.submitting.set(true);
      return this.moduleInstanceService
        .saveReviseInstance({
          contractId,
          moduleId,
          screenId,
          workflowId,
          entityInstanceId,
          transitionId: transition.id,
          roleId: transition.roles?.length ? transition.roles[0].key : null,
        })
        .pipe(
          finalize(() => {
            this.submitting.set(false);
            this.selectedTransition.set(null);
          }),
        )
        .subscribe(newEntityInstanceId => {
          this.setDocumentAsDirty();
          if (this.closeIfOpenedFromDashboard()) {
            return;
          }
          this.reloadAfterAddNew(newEntityInstanceId);
        });
    }
    if (transition.otherModule) {
      const { moduleId, perContract, screenId } = transition.otherModule;
      return this.documentService
        .openDocument({
          moduleId,
          contractId: perContract ? contractId : null,
          screenId,
          entityInstanceId: 0,
        })
        .afterClosed()
        .subscribe(info => this.handleRelationDocument(info));
    }
    if (transition.isAutoTrigger) {
      /* Perform autoTrigger */
      this.submitting.set(true);
      return this.moduleInstanceCommonService
        .saveTransitionScreen(
          {
            entityInstanceId,
            contractId,
            moduleId,
            workflowId,
            roleId: transition.roles?.length ? transition.roles[0].key : null,
            transitionId: transition.id,
          },
          {},
        )
        .pipe(
          finalize(() => {
            this.submitting.set(false);
            this.selectedTransition.set(null);
          }),
        )
        .subscribe(() => {
          this.setDocumentAsDirty();
          if (this.closeIfOpenedFromDashboard()) {
            return;
          }
          this.dmStore.loadGeneralInformation({ entityInstanceId, contractId, moduleId });
        });
    }
    /* Start the transition in the right sidebar. At this moment, the main form is pristine - no need to check formulas.
    This also sets dmStore's actionsDisabled: true */
    return this.dmStore.startTransition(transition, this.selectedEntityInstanceId()).subscribe(transitionData => {
      if (!transitionData.gridFields?.fields?.length && !transitionData.notificationData?.notifyOptions?.length) {
        this.executeTransition({});
      }
    });
  }

  //   MARK: Save / Transition
  private saveDraft(values?: IdValueType<string, number>[], silent = false) {
    const { contractId, entityInstanceId, moduleId, workflowId, screenId } = this.currentDocument().params;
    let request: Observable<number>;
    if (this.presetData()) {
      request = this.moduleInstanceService.saveTemplateInstance({
        moduleId,
        entityInstanceId,
        workflowId,
        contractId,
        transitionId: 0,
        presetId: this.presetData().id,
        body: {
          values,
          screenId,
          relatedDocuments: this.relatedDocuments.length ? this.relatedDocuments : null,
          workflowGroupId: this.workflowGroupId(),
        },
      });
    } else {
      request = this.moduleInstanceService.saveDraftInstance({
        moduleId,
        entityInstanceId,
        workflowId,
        contractId,
        body: {
          values,
          screenId,
          relatedDocuments: this.relatedDocuments.length ? this.relatedDocuments : null,
          documentNumber: this.docNumber()?.key ?? null,
          workflowGroupId: this.workflowGroupId(),
        },
      });
    }
    request
      .pipe(
        finalize(() => {
          this.submitting.set(false);
          this.editing.set(true);
          this.selectedTransition.set(null);
          if (this.autosaveInProgress() && !this.markedForAutosave()) {
            this.autosaveInProgress.set(false);
          }
        }),
      )
      .subscribe({
        next: newEntityInstanceId => {
          this.notify.success('messages.draftSaved');
          this.form.markAsPristine();
          if (newEntityInstanceId && !entityInstanceId) {
            this.reloadAfterAddNew(newEntityInstanceId, silent);
          }
          if (this.presetData()) {
            this.presetData.set(null);
          }
          this.relatedDocuments = [];
          this.setDocumentAsDirty();
          this.saveResult.emit(true);
        },
        error: ({ error }: { error: CipoHttpErrorResponse }) => {
          this.saveResult.emit(false);
          const errors = mapFieldErrors(error, this.gridFields()?.fields);
          if (!errors.length) {
            return;
          }
          errors.forEach(({ key, message }) => this.form.controls[key]?.setErrors({ custom: message }));
        },
      });
  }

  private saveDocument(values: IdValueType<string, number>[]) {
    const { contractId, entityInstanceId, moduleId, screenId } = this.currentDocument().params;
    return this.moduleInstanceService
      .updateFieldValuesDocument({
        moduleId,
        entityInstanceId,
        contractId,
        body: { values, screenId, workflowGroupId: this.workflowGroupId() },
      })
      .pipe(
        finalize(() => {
          this.submitting.set(false);
          this.selectedTransition.set(null);
        }),
      )
      .subscribe({
        next: () => {
          this.notify.success('messages.changesSaved');
          this.dmStore.loadGeneralInformation({ contractId, moduleId, entityInstanceId });
          this.loadGridFields();
          this.setDocumentAsDirty();
          this.saveResult.emit(true);
        },
        error: () => this.saveResult.emit(false),
      });
  }

  /** Execute a transition, called on transition form submit (after formulas are calculated)
   */
  executeTransition(transitionFormValues: Partial<TransitionFormValue>) {
    if (!this.checkFormValid(this.transitionForm)) {
      return;
    }
    this.submitting.set(true);
    this.editing.set(false);
    const data = this.isDraft()
      ? { ...transitionFormValues, values: this.dynamicFormRef().getFormValue() }
      : transitionFormValues;
    this.documentDetailsService
      .submitTransition({
        params: this.currentDocument().params,
        transition: this.selectedTransition(),
        data,
        documentNumber: this.docNumber()?.key ?? null,
        assignee: this.assignee(),
        workflowGroupId: this.workflowGroupId(),
        presetId: this.presetData()?.id,
      })
      .pipe(
        finalize(() => {
          this.submitting.set(false);
        }),
      )
      .subscribe(newEntityInstanceId => {
        if (this.presetData()) {
          this.presetData.set(null);
        }
        this.selectedTransition.set(null);
        this.dmStore.cancelTransition(this.currentDocument().params.entityInstanceId);
        const { entityInstanceId, contractId, moduleId } = this.currentDocument().params;

        this.setDocumentAsDirty();

        if (this.closeIfOpenedFromDashboard()) {
          return;
        }

        if (!entityInstanceId) {
          /* This case handles new documents (not draft, NEW) that are entering the workflow - submit or any other transition */
          this.reloadAfterAddNew(newEntityInstanceId);
        } else if (newEntityInstanceId) {
          /* This case handles existing documents that have transitioned to a new workflow state - load document and tabs */
          this.reloadDocument();
        } else {
          /* This case handles comment action - load activity tab only */
          this.dmStore.loadActivityInstances({ contractId, moduleId, entityInstanceId });
        }
      });
  }

  private handleRelationDocument(info: Partial<DocumentInfoModel>) {
    /* If the document didn't enter the workflow, stop the process */
    if (!info || info.isDraftState) {
      this.selectedTransition.set(null);
      return;
    }
    const selectedTransition = this.selectedTransition();
    const otherModule = selectedTransition?.otherModule;
    if (!otherModule) {
      this.selectedTransition.set(null);
      return;
    }
    const { currentFieldId, hasMultipleValues } = otherModule;
    const { contractId, moduleId, entityInstanceId } = this.currentDocument().params;
    const isDraft = this.isDraft();
    if (!isDraft) {
      this.submitting.set(true);
    }
    const obs = isDraft
      ? of(null)
      : this.moduleInstanceService.updateFieldValuesDocumentOnRelatedDocument({
          body: {
            ...otherModule,
            entityInstanceId: info.entityInstanceId,
          },
          contractId,
          moduleId,
          entityInstanceId,
        });
    obs
      .pipe(
        finalize(() => {
          if (!isDraft) {
            this.submitting.set(false);
          }

          this.selectedTransition.set(null);
        }),
      )
      .subscribe(() => {
        let fieldFound = false;
        this.gridFields.update(({ fields, ...rest }) => ({
          ...rest,
          fields: fields.map(f => {
            if (f.fieldOptions.id !== currentFieldId) {
              return f;
            }

            fieldFound = true;
            const existingValue: string[] | string = this.form.controls[currentFieldId].value;
            const value = !hasMultipleValues
              ? info.entityInstanceId.toString()
              : [...new Set([...((existingValue as string[]) ?? []), info.entityInstanceId.toString()])];
            this.form.controls[currentFieldId].setValue(value);
            const { relationId } = f.fieldOptions.dataSourceInfo;
            const dataSourceKey = makeKey({
              contractId,
              moduleId,
              entityInstanceId,
              fieldId: currentFieldId,
              selectedIds: hasMultipleValues ? (existingValue as string[]) : [existingValue as string],
              relationId,
            });
            const dataSource = this.dictionaryStore.dynamic()[dataSourceKey]?.data;
            return {
              ...f,
              value,
              fieldOptions: {
                ...f.fieldOptions,
                options: [
                  ...(dataSource || f.fieldOptions.options || []),
                  { key: info.entityInstanceId.toString(), value: info.moduleDocumentNumber },
                ],
              },
            };
          }),
        }));
        if (isDraft) {
          if (!fieldFound) {
            this.relatedDocuments.push({
              id: currentFieldId,
              value: hasMultipleValues ? [info.entityInstanceId] : info.entityInstanceId,
            });
          }
        } else {
          this.dmStore.loadRelations({ contractId, moduleId, entityInstanceId });
          this.dmStore.loadActivityInstances({ contractId, moduleId, entityInstanceId });
        }
      });
  }
}
